import { useMemo } from "react";
import { Box, CheckCircle2, Copy, MapPin, Pencil, Plus, Wrench } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { MAQUINARIO_STATUS_LABELS, type MaquinarioDto } from "@/services/locacoes";
import { MAQUINARIO_STATUS_THEME } from "./statusTheme";
import {
  locacoesSectionTitleClass,
  locacoesTabContentClass,
  locacoesTableHeadClass,
  locacoesTableWrapClass,
} from "./locacoesUtils";
import { cn } from "@/utils/cn";
import { LocacoesStatCard } from "./LocacoesStatCard";
import { LocacoesSubToolbar } from "./LocacoesSubToolbar";

interface Props {
  equipamentos: MaquinarioDto[];
  onNovoEquipamento: () => void;
  onEditEquipamento: (m: MaquinarioDto) => void;
  onDuplicateEquipamento: (m: MaquinarioDto) => void;
}

export function TabCadastroEquipamentos({
  equipamentos,
  onNovoEquipamento,
  onEditEquipamento,
  onDuplicateEquipamento,
}: Props) {
  const kpi = useMemo(
    () => ({
      total: equipamentos.length,
      disponiveis: equipamentos.filter((m) => m.status === "disponivel").length,
      locados: equipamentos.filter((m) => m.status === "locado").length,
      manutencao: equipamentos.filter((m) => m.status === "manutencao").length,
    }),
    [equipamentos],
  );

  return (
    <div className={locacoesTabContentClass}>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
        <LocacoesStatCard title="Total" value={kpi.total} icon={<Box className="h-5 w-5" />} accentClass="border-l-4 border-l-blue-400" />
        <LocacoesStatCard title="Disponíveis" value={kpi.disponiveis} icon={<CheckCircle2 className="h-5 w-5" />} accentClass="border-l-4 border-l-emerald-400" />
        <LocacoesStatCard title="Locados" value={kpi.locados} icon={<MapPin className="h-5 w-5" />} accentClass="border-l-4 border-l-sky-400" />
        <LocacoesStatCard title="Em manutenção" value={kpi.manutencao} icon={<Wrench className="h-5 w-5" />} accentClass="border-l-4 border-l-amber-400" />
      </div>

      <LocacoesSubToolbar
        title="Cadastro"
        end={
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={onNovoEquipamento}>
            Novo equipamento
          </Button>
        }
      >
        <div className={locacoesSectionTitleClass}>
          <Box className="h-4 w-4 text-brand-600" />
          <span>Equipamentos cadastrados ({equipamentos.length})</span>
        </div>
        <p className="mt-2 text-sm text-slate-500">
          Containers, baús, módulos e demais ativos passivos para contratos de locação por mensalidade.
        </p>
      </LocacoesSubToolbar>

      <div className={locacoesTableWrapClass}>
        <table className="min-w-full text-sm">
          <thead className={locacoesTableHeadClass}>
            <tr>
              <th className="px-4 py-3">Nome</th>
              <th className="px-4 py-3">Tipo</th>
              <th className="px-4 py-3">Código</th>
              <th className="px-4 py-3">Modelo</th>
              <th className="px-4 py-3">Placa</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Ações</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {equipamentos.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-slate-500">
                  Nenhum equipamento cadastrado.
                </td>
              </tr>
            ) : (
              equipamentos.map((m) => {
                const theme = MAQUINARIO_STATUS_THEME[m.status];
                return (
                  <tr key={m.id} className="hover:bg-slate-50/80">
                    <td className="px-4 py-3 font-medium text-slate-800">{m.nome}</td>
                    <td className="px-4 py-3 text-slate-600">{m.tipo || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{m.codigoInterno || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{m.modelo || "—"}</td>
                    <td className="px-4 py-3 text-slate-600">{m.placa || "—"}</td>
                    <td className="px-4 py-3">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                          theme.badge,
                        )}
                      >
                        <span className={cn("h-1.5 w-1.5 rounded-full", theme.dot)} />
                        {MAQUINARIO_STATUS_LABELS[m.status]}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onEditEquipamento(m)}
                          leftIcon={<Pencil className="h-3.5 w-3.5" />}
                        >
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => onDuplicateEquipamento(m)}
                          leftIcon={<Copy className="h-3.5 w-3.5" />}
                          title="Duplicar"
                        >
                          Duplicar
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
